/**
 * Pure, deterministic title-page / cover-page formatter.
 *
 * Takes a small set of form values plus a style and returns a structured
 * preview (blocks of aligned lines) and a Word-ready HTML document. This module
 * does NOT touch the citation engine — it emits a title page, not a citation —
 * so there is zero risk to the formatting core.
 *
 * Specs followed:
 * - APA 7 student & professional title page:
 *   https://apastyle.apa.org/style-grammar-guidelines/paper-format/title-page
 * - MLA 9 first-page heading (Purdue OWL):
 *   https://owl.purdue.edu/owl/research_and_citation/mla_style/mla_formatting_and_style_guide/mla_general_format.html
 */

export type TitlePageStyle = "apa-student" | "apa-professional" | "mla";

export interface TitlePageInput {
  /** Paper title. */
  title: string;
  /** Author name(s) — one per line or comma-separated. */
  authors: string;
  /** Department, University (APA affiliation / MLA institution). */
  affiliation: string;
  /** Course number, e.g. "PSY 101". */
  courseNumber: string;
  /** Course name, e.g. "Introduction to Psychology". */
  courseName: string;
  /** Instructor name. */
  instructor: string;
  /** Due date, e.g. "March 14, 2026". */
  dueDate: string;
  /** Running head (APA professional only, max 50 chars, shown uppercase). */
  runningHead: string;
  /** Author note paragraph(s) (APA professional only). */
  authorNote: string;
}

export interface PreviewLine {
  text: string;
  bold?: boolean;
}

export interface PreviewBlock {
  align: "center" | "left";
  lines: PreviewLine[];
  /** A blank double-spaced line before this block (APA: after the title). */
  spaceBefore?: boolean;
}

export interface TitlePagePreview {
  /** Page header: optional left text (running head) + right text (page number). */
  header: { left?: string; right: string };
  blocks: PreviewBlock[];
  /** Layout hint for rendering — APA centers in the upper-middle, MLA is top-left. */
  layout: "apa" | "mla";
}

const RUNNING_HEAD_MAX = 50;
const NAME_SPLIT_REGEX = /[\n,]+/;
const WHITESPACE_REGEX = /\s+/;
const PARAGRAPH_SPLIT_REGEX = /\n+/;

function splitAuthors(authors: string): string[] {
  return authors
    .split(NAME_SPLIT_REGEX)
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

/** Join names as "A", "A and B", or "A, B, and C" (serial comma). */
function formatAuthorList(names: string[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return names[0] ?? "";
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  const head = names.slice(0, -1).join(", ");
  const last = names.at(-1);
  return `${head}, and ${last}`;
}

/** Last name of the first author, used for the MLA running header. */
function firstAuthorSurname(authors: string): string {
  const first = splitAuthors(authors)[0] ?? "";
  const parts = first.split(WHITESPACE_REGEX).filter(Boolean);
  return parts.at(-1) ?? "";
}

/** "PSY 101: Introduction to Psychology", or whichever part is present. */
function formatCourse(input: TitlePageInput): string {
  const num = input.courseNumber.trim();
  const name = input.courseName.trim();
  if (num && name) {
    return `${num}: ${name}`;
  }
  return num || name;
}

function centerBlock(text: string, bold = false): PreviewBlock {
  const line: PreviewLine = bold ? { text, bold } : { text };
  return { align: "center", lines: [line] };
}

function nonEmptyCenterBlocks(values: string[]): PreviewBlock[] {
  return values
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => centerBlock(v));
}

function buildApa(
  input: TitlePageInput,
  professional: boolean
): TitlePagePreview {
  const authors = formatAuthorList(splitAuthors(input.authors));
  const blocks: PreviewBlock[] = [];

  if (input.title.trim()) {
    blocks.push(centerBlock(input.title.trim(), true));
  }
  if (authors) {
    blocks.push({ ...centerBlock(authors), spaceBefore: blocks.length > 0 });
  }
  if (input.affiliation.trim()) {
    blocks.push(centerBlock(input.affiliation.trim()));
  }

  if (professional) {
    addProfessionalAuthorNote(blocks, input);
  } else {
    blocks.push(
      ...nonEmptyCenterBlocks([
        formatCourse(input),
        input.instructor,
        input.dueDate,
      ])
    );
  }

  const left = professional
    ? input.runningHead.trim().slice(0, RUNNING_HEAD_MAX).toUpperCase()
    : undefined;

  return { header: { left, right: "1" }, blocks, layout: "apa" };
}

function addProfessionalAuthorNote(
  blocks: PreviewBlock[],
  input: TitlePageInput
): void {
  const note = input.authorNote.trim();
  if (!note) {
    return;
  }
  blocks.push({ ...centerBlock("Author Note", true), spaceBefore: true });
  const paragraphs = note
    .split(PARAGRAPH_SPLIT_REGEX)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  for (const para of paragraphs) {
    blocks.push({ align: "left", lines: [{ text: para }] });
  }
}

function buildMla(input: TitlePageInput): TitlePagePreview {
  const headingLines: PreviewLine[] = [
    formatAuthorList(splitAuthors(input.authors)),
    input.instructor.trim(),
    formatCourse(input),
    input.dueDate.trim(),
  ]
    .filter((text) => text.length > 0)
    .map((text) => ({ text }));

  const blocks: PreviewBlock[] = [];
  if (headingLines.length > 0) {
    blocks.push({ align: "left", lines: headingLines });
  }
  if (input.title.trim()) {
    blocks.push(centerBlock(input.title.trim()));
  }

  const surname = firstAuthorSurname(input.authors);
  const right = surname ? `${surname} 1` : "1";
  return { header: { right }, blocks, layout: "mla" };
}

export function buildTitlePage(
  style: TitlePageStyle,
  input: TitlePageInput
): TitlePagePreview {
  if (style === "mla") {
    return buildMla(input);
  }
  return buildApa(input, style === "apa-professional");
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const BLANK_LINE = '<p style="margin:0">&nbsp;</p>';
const DOCUMENT_FONT =
  "font-family:'Times New Roman',Times,serif;font-size:12pt;line-height:2";
/** APA 7: the title sits three to four double-spaced lines below the top margin. */
const APA_TOP_BLANK_LINES = 3;

function blockToHtml(block: PreviewBlock): string {
  const inner = block.lines
    .map((line) => {
      const text = escapeHtml(line.text);
      return line.bold ? `<strong>${text}</strong>` : text;
    })
    .join("<br>");
  const gap = block.spaceBefore ? `${BLANK_LINE}\n` : "";
  return `${gap}<p style="text-align:${block.align};margin:0">${inner}</p>`;
}

/** The page header (running head left, page number right) as a table Word can paste. */
function headerToHtml(preview: TitlePagePreview): string {
  const left = preview.header.left ? escapeHtml(preview.header.left) : "&nbsp;";
  return `<table style="width:100%;border-collapse:collapse"><tr><td style="text-align:left;padding:0">${left}</td><td style="text-align:right;padding:0">${escapeHtml(preview.header.right)}</td></tr></table>`;
}

/**
 * The title page as an HTML fragment for the clipboard: inline styles only, so pasting into
 * Word or Google Docs keeps Times New Roman 12 pt, double spacing, alignment and bold.
 */
export function buildTitlePageFragment(
  style: TitlePageStyle,
  input: TitlePageInput
): string {
  const preview = buildTitlePage(style, input);
  const topPad =
    preview.layout === "apa"
      ? `${BLANK_LINE}\n`.repeat(APA_TOP_BLANK_LINES)
      : "";
  const body = preview.blocks.map(blockToHtml).join("\n");
  return `<div style="${DOCUMENT_FONT}">\n${headerToHtml(preview)}\n${topPad}${body}\n</div>`;
}

/** The same page as plain text, for targets that take no HTML. */
export function buildTitlePagePlainText(
  style: TitlePageStyle,
  input: TitlePageInput
): string {
  const preview = buildTitlePage(style, input);
  const parts: string[] = [];
  for (const block of preview.blocks) {
    if (block.spaceBefore) {
      parts.push("");
    }
    parts.push(...block.lines.map((line) => line.text));
  }
  return parts.join("\n");
}

/**
 * A self-contained HTML document a student can save as `.doc` / print.
 * Times New Roman 12pt, double-spaced, 1-inch margins, per APA/MLA defaults.
 */
export function buildTitlePageWordHtml(
  style: TitlePageStyle,
  input: TitlePageInput
): string {
  const docTitle = input.title.trim() || "Title Page";
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${escapeHtml(docTitle)}</title>
<style>
body { font-family: "Times New Roman", Times, serif; font-size: 12pt; line-height: 2; margin: 1in; }
p { margin: 0; padding: 0; }
</style>
</head>
<body>
${buildTitlePageFragment(style, input)}
</body>
</html>`;
}
